// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-SUPPORT-3: Support Home — the support tier's landing dashboard.
 * Active customers with user counts, open-ticket load, and the PRIMARY /
 * SECONDARY technician owning each customer. Assignment editing is
 * supervisor+ (support.tickets.manage); agents see names read-only.
 * Sortable columns (click header); Excel-grade column manager arrives with
 * the queue-grid unification (D-SUPPORT-3 §UI).
 */
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'
import { Users, Inbox, AlertTriangle, Building2, ChevronRight, ChevronDown } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { useAuth } from '../../auth/AuthContext'
import { ErrorBanner, LoadingState, EmptyState } from '../../components/workflow/StatePills'
import SearchableSelect from '../../components/common/SearchableSelect'

// D-SUPPORT-13 (Patrick, 2026-08-22): "the customer health info need to be cached... expand
// should stick and survive between switching tabs or screens or refresh, make the TTL 10 min."
//
// Fetching moves to TanStack Query, which this app already mounts and which the E-rules require
// for ALL fetching. This page hand-rolled useState + a manual fetch that cleared the payload on
// every toggle, so every switch between customers hit the server — a ~4 second call. That was a
// deviation from the house pattern, not a missing capability.
const DETAIL_TTL_MS = 10 * 60 * 1000

// gcTime MUST exceed staleTime, and that is the part that actually matters. Collapsing a row
// unmounts its query, and TanStack's default gcTime is FIVE minutes — shorter than the 10-minute
// TTL — so the cache would be evicted at minute 5 and re-expanding at minute 6 would refetch
// despite the data being "fresh". A 10-minute TTL with a 5-minute cache is a 5-minute TTL.
const DETAIL_GC_MS = 30 * 60 * 1000

// Expansion and per-customer tab survive a reload, a screen change and a browser-tab switch
// because they live in localStorage. This is view state, not data.
const EXPANDED_KEY = 'owlrx.support.customers.expanded'
const TABS_KEY = 'owlrx.support.customers.tabs'
type DetailTab = 'usage' | 'cases' | 'users' | 'activity'

function loadExpanded(): string[] {
  try { const v = JSON.parse(localStorage.getItem(EXPANDED_KEY) ?? '[]'); return Array.isArray(v) ? v.filter(x => typeof x === 'string') : [] }
  catch { return [] }
}
function loadTabs(): Record<string, DetailTab> {
  try { const v = JSON.parse(localStorage.getItem(TABS_KEY) ?? '{}'); return v && typeof v === 'object' ? v : {} }
  catch { return {} }
}

type SortKey = 'health_score' | 'name' | 'onboarding_state' | 'user_count' | 'open_tickets' | 'primary_name' | 'secondary_name'

export default function SupportHomePage() {
  const navigate = useNavigate()
  const { hasPermission } = useAuth()
  const canManage = hasPermission('support.tickets.manage')
  const [customers, setCustomers] = useState<api.SupportCustomer[]>([])
  const [team, setTeam] = useState<api.SupportTeamMember[]>([])
  // D-SUPPORT-8: expand a customer in place rather than navigating away — the technician dropdowns
  // stay on screen, which is the thing an agent usually wants to change once they have looked.
  // A LIST, not a single id — several customers stay open at once so they can be compared side
  // by side instead of one at a time.
  const [openIds, setOpenIds] = useState<string[]>(loadExpanded)
  const [tabs, setTabs] = useState<Record<string, DetailTab>>(loadTabs)

  useEffect(() => { try { localStorage.setItem(EXPANDED_KEY, JSON.stringify(openIds)) } catch { /* private mode */ } }, [openIds])
  useEffect(() => { try { localStorage.setItem(TABS_KEY, JSON.stringify(tabs)) } catch { /* private mode */ } }, [tabs])

  // No fetching here any more. Each expanded row owns its own cached query, so toggling is pure
  // view state and costs nothing.
  const toggle = (id: string) =>
    setOpenIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const setTabFor = (id: string, t: DetailTab) => setTabs(prev => ({ ...prev, [id]: t }))
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)
  // Risk-first by default: the entire point of scoring is that the worst customer is the first row
  // you see. Ascending on score puts the lowest score — the most at risk — at the top.
  const [sortKey, setSortKey] = useState<SortKey>('health_score')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const load = () => {
    setLoading(true)
    Promise.all([api.listSupportCustomers(), api.listSupportTeam()])
      .then(([c, t]) => { setCustomers(c.customers); setTeam(t.team) })
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }
  useEffect(load, [])

  const sorted = useMemo(() => {
    const arr = [...customers]
    arr.sort((a, b) => {
      const av = a[sortKey] ?? '', bv = b[sortKey] ?? ''
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? cmp : -cmp
    })
    return arr
  }, [customers, sortKey, sortDir])

  const clickSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(k); setSortDir('asc') }
  }

  const setAssign = async (c: api.SupportCustomer, which: 'primary' | 'secondary', accountId: string) => {
    setSaving(c.id)
    const pri = which === 'primary' ? (accountId || null) : c.primary_account_id
    const sec = which === 'secondary' ? (accountId || null) : c.secondary_account_id
    try {
      await api.setSupportAssignments(c.id, pri, sec)
      setCustomers(prev => prev.map(x => x.id === c.id ? {
        ...x,
        primary_account_id: pri, primary_name: team.find(t => t.id === pri)?.display_name ?? null,
        secondary_account_id: sec, secondary_name: team.find(t => t.id === sec)?.display_name ?? null,
      } : x))
    } catch (e) { setError(friendlyError(e)) }
    finally { setSaving(null) }
  }

  const totals = useMemo(() => ({
    customers: customers.length,
    users: customers.reduce((s, c) => s + c.user_count, 0),
    open: customers.reduce((s, c) => s + c.open_tickets, 0),
    critical: customers.reduce((s, c) => s + c.open_critical, 0),
  }), [customers])

  return (
    <div style={{ padding: '24px 24px 48px', maxWidth: 'var(--page-max, 1280px)', margin: '0 auto', width: '100%' }}>
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Support home</h1>
        <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginTop: 4 }}>
          Every active customer, its users, open load, and who owns it.
        </div>
      </header>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <Kpi icon={<Building2 size={15} />} label="Active customers" value={totals.customers} />
        <Kpi icon={<Users size={15} />} label="Total users" value={totals.users} />
        <Kpi icon={<Inbox size={15} />} label="Open tickets" value={totals.open} onClick={() => navigate('/admin/support')} />
        <Kpi icon={<AlertTriangle size={15} />} label="Open critical" value={totals.critical} tone={totals.critical > 0 ? 'bad' : 'ok'} />
      </div>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 8, overflowX: 'auto' }}>
        {loading ? <LoadingState label="Loading customers…" /> :
         customers.length === 0 ? <EmptyState message="No active customers." /> :
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 860 }}>
          <thead>
            <tr>
              <Th label="Health" k="health_score" sortKey={sortKey} sortDir={sortDir} onSort={clickSort} />
              <Th label="Customer" k="name" sortKey={sortKey} sortDir={sortDir} onSort={clickSort} />
              <Th label="State" k="onboarding_state" sortKey={sortKey} sortDir={sortDir} onSort={clickSort} />
              <Th label="Users" k="user_count" sortKey={sortKey} sortDir={sortDir} onSort={clickSort} />
              <Th label="Open tickets" k="open_tickets" sortKey={sortKey} sortDir={sortDir} onSort={clickSort} />
              <Th label="Primary technician" k="primary_name" sortKey={sortKey} sortDir={sortDir} onSort={clickSort} />
              <Th label="Secondary technician" k="secondary_name" sortKey={sortKey} sortDir={sortDir} onSort={clickSort} />
            </tr>
          </thead>
          <tbody>
            {sorted.map(c => (
              // Fragment, not <>, because a mapped list needs the key on the OUTERMOST element and
              // the expanded detail is a second sibling row.
              <Fragment key={c.id}>
              <tr style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ ...td, width: 112 }}><HealthPill c={c} /></td>
                <td style={{ ...td, fontWeight: 600 }}>
                  <button onClick={() => toggle(c.id)} title="Show cases, users and activity"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'none',
                             cursor: 'pointer', padding: 0, font: 'inherit', color: 'var(--color-text)', textAlign: 'left' }}>
                    {openIds.includes(c.id) ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
                    {c.name}
                  </button>
                </td>
                <td style={td}><StatePill state={c.onboarding_state} /></td>
                <td style={td}>{c.user_count}</td>
                <td style={td}>
                  {c.open_tickets}
                  {c.open_critical > 0 && <span style={critChip} title={`${c.open_critical} critical`}>{c.open_critical} crit</span>}
                </td>
                <td style={td}><TechCell c={c} which="primary" team={team} canManage={canManage} saving={saving === c.id} onSet={setAssign} /></td>
                <td style={td}><TechCell c={c} which="secondary" team={team} canManage={canManage} saving={saving === c.id} onSet={setAssign} /></td>
              </tr>
              {openIds.includes(c.id) && (
                <tr>
                  <td colSpan={7} style={{ padding: 0, background: 'var(--color-bg)', borderTop: '1px solid var(--color-border)' }}>
                    <CustomerDetail pharmacyId={c.id} tab={tabs[c.id] ?? 'usage'}
                                    setTab={t => setTabFor(c.id, t)} nav={navigate} />
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>}
      </div>
    </div>
  )
}

function TechCell({ c, which, team, canManage, saving, onSet }: {
  c: api.SupportCustomer; which: 'primary' | 'secondary'; team: api.SupportTeamMember[]
  canManage: boolean; saving: boolean
  onSet: (c: api.SupportCustomer, which: 'primary' | 'secondary', accountId: string) => void
}) {
  const current = which === 'primary' ? c.primary_account_id : c.secondary_account_id
  const name = which === 'primary' ? c.primary_name : c.secondary_name
  if (!canManage) return <span style={{ color: name ? 'inherit' : 'var(--color-text-mute)' }}>{name ?? 'Unassigned'}</span>
  return (
    <SearchableSelect
      value={current ?? ''}
      onChange={v => { if (!saving) onSet(c, which, v) }}
      ariaLabel={`${which} technician for ${c.name}`}
      placeholder="Unassigned"
      options={[{ value: '', label: 'Unassigned' }, ...team.map(t => ({ value: t.id, label: t.display_name ?? t.email }))]}
      style={{ minWidth: 150, fontSize: 13 }}
    />
  )
}

function Th({ label, k, sortKey, sortDir, onSort }: { label: string; k: SortKey; sortKey: SortKey; sortDir: string; onSort: (k: SortKey) => void }) {
  return (
    <th onClick={() => onSort(k)}
        style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--color-text-mute)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.4px', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      {label}{sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  )
}

function Kpi({ icon, label, value, tone, onClick }: { icon: React.ReactNode; label: string; value: number; tone?: 'ok' | 'bad'; onClick?: () => void }) {
  return (
    <div onClick={onClick}
         style={{ flex: '1 1 160px', minWidth: 140, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '10px 12px', cursor: onClick ? 'pointer' : 'default' }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--color-text-mute)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>{icon}{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2, color: tone === 'bad' ? '#b91c1c' : 'var(--color-text)' }}>{value}</div>
    </div>
  )
}

function StatePill({ state }: { state: string | null }) {
  const s = state ?? 'unknown'
  const good = s === 'active'
  return <span style={{ display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: good ? '#dcfce7' : '#fef3c7', color: good ? '#15803d' : '#b45309' }}>{s.replace(/_/g, ' ')}</span>
}

const td: React.CSSProperties = { padding: '10px 10px' }
const critChip: React.CSSProperties = { marginLeft: 7, fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: '#fee2e2', color: '#b91c1c' }

// ---------------------------------------------------------------- customer detail
// D-SUPPORT-8: cases / users / activity for one customer, inline under its row.
// The activity tab is a REDUCED audit projection — action, entity type, actor, time. Payloads
// (before/after/metadata, entity ids) are omitted server-side because they can embed patient data.
function CustomerDetail({ pharmacyId, tab, setTab, nav }: {
  pharmacyId: string
  tab: DetailTab
  setTab: (t: DetailTab) => void
  nav: ReturnType<typeof useNavigate>
}) {
  // Day under the cursor in the login-traffic chart. Lives here, not in the usage tab's IIFE —
  // hooks cannot be called inside that closure.
  const [hoverDay, setHoverDay] = useState<{ day: string; logins: number; users: number } | null>(null)

  // Each expanded row owns its query, keyed by pharmacy. Collapsing and re-expanding, switching
  // between customers, or changing screens all hit this cache rather than the server — and the
  // server call is a ~4 second one, so the difference is the whole point.
  const { data: detail, isPending, error, dataUpdatedAt, isFetching, refetch } = useQuery({
    queryKey: ['support-customer-detail', pharmacyId],
    queryFn: () => api.getSupportCustomerDetail(pharmacyId),
    staleTime: DETAIL_TTL_MS,
    gcTime: DETAIL_GC_MS,
  })

  if (isPending) return <div style={{ padding: 14 }}><LoadingState label="Loading customer…" /></div>
  if (error) return <div style={{ padding: 14 }}><ErrorBanner message={friendlyError(error)} /></div>
  if (!detail) return null

  // The TTL is stated, not hidden. A cached health score with no age on it is a number you cannot
  // judge — "is this now, or from before the incident I am looking at?"
  const ageMin = Math.floor((Date.now() - dataUpdatedAt) / 60000)
  const freshness = isFetching ? 'refreshing…'
    : ageMin < 1 ? 'just now'
    : ageMin === 1 ? '1 min ago'
    : `${ageMin} min ago`

  const open = detail.cases.filter(c => !['solved', 'closed'].includes(c.status))
  const closed = detail.cases.filter(c => ['solved', 'closed'].includes(c.status))
  const tabBtn = (t: typeof tab): React.CSSProperties => ({
    padding: '7px 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
    border: 'none', background: 'none', borderBottom: tab === t ? '2px solid #0891b2' : '2px solid transparent',
    color: tab === t ? '#0e7490' : 'var(--color-text-mute)',
  })

  return (
    <div style={{ padding: '4px 14px 14px' }}>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--color-border)', marginBottom: 10, alignItems: 'center' }}>
        <button style={tabBtn('usage')} onClick={() => setTab('usage')}>Usage</button>
        <button style={tabBtn('cases')} onClick={() => setTab('cases')}>Cases ({open.length} open)</button>
        <button style={tabBtn('users')} onClick={() => setTab('users')}>Users ({detail.users.length})</button>
        <button style={tabBtn('activity')} onClick={() => setTab('activity')}>Activity ({detail.activity.length})</button>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 11.5, color: 'var(--color-text-mute)' }}>
          <span title={`Cached for ${Math.round(DETAIL_TTL_MS / 60000)} minutes, then refreshed on next view`}>
            as of {freshness}
          </span>
          <button onClick={() => refetch()} disabled={isFetching} title="Refresh this customer now"
            style={{ border: 'none', background: 'none', cursor: isFetching ? 'default' : 'pointer',
                     padding: 2, color: 'inherit', display: 'inline-flex', alignItems: 'center' }}>
            <RefreshCw size={13} aria-hidden="true" />
          </button>
        </span>
      </div>

      {tab === 'usage' && (() => {
        const u = detail.usage
        const h = detail.health
        const ing = detail.ingest
        const maxLogins = Math.max(1, ...u.daily.map(d => d.logins))
        const tone = u.adoption_pct >= 60 ? '#047857' : u.adoption_pct >= 25 ? '#b45309' : '#b91c1c'
        const bandTone = h.band === 'healthy' ? '#047857' : h.band === 'watch' ? '#b45309' : '#b91c1c'
        const bandBg   = h.band === 'healthy' ? '#ecfdf5' : h.band === 'watch' ? '#fffbeb' : '#fef2f2'
        const flagTone = (s: string) => s === 'bad' ? '#b91c1c' : s === 'warn' ? '#b45309' : 'var(--color-text-mute)'
        return (
        <div>
          {/* Health first: the score is only a sort key — the REASONS are what a CSM acts on, so
              they are listed rather than hidden behind the number. */}
          <div style={{ border: `1px solid ${bandTone}33`, background: bandBg, borderRadius: 10, padding: '10px 12px', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: bandTone, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                {h.band === 'at_risk' ? 'At risk' : h.band === 'watch' ? 'Watch' : 'Healthy'}
              </span>
              <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>score {h.score}/100</span>
              {h.sla.attainment_pct !== null && (
                <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>
                  · SLA attainment <strong style={{ color: h.sla.attainment_pct >= 90 ? '#047857' : h.sla.attainment_pct >= 70 ? '#b45309' : '#b91c1c' }}>
                    {h.sla.attainment_pct}%
                  </strong> ({h.sla.met} met / {h.sla.breached} breached of {h.sla.measured})
                </span>
              )}
            </div>
            {h.flags.length === 0
              ? <div style={{ fontSize: 12.5, marginTop: 6, color: '#047857' }}>No risk signals.</div>
              : <ul style={{ margin: '7px 0 0', paddingLeft: 18, fontSize: 12.5 }}>
                  {h.flags.map(f => (
                    <li key={f.code} style={{ color: flagTone(f.severity), marginBottom: 2 }}>{f.detail}</li>
                  ))}
                </ul>}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            <MiniKpi label={`Adoption (${u.window_days}d)`} value={`${u.adoption_pct}%`} tone={tone}
                     sub={`${u.active_users} of ${u.total_users} users signed in`} />
            <MiniKpi label="Logins" value={String(u.logins)} sub={`last ${u.window_days} days`} />
            <MiniKpi label="Last login"
                     value={u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'never'}
                     sub={u.last_login_at ? new Date(u.last_login_at).toLocaleTimeString() : 'no recorded sign-in'}
                     tone={u.last_login_at ? undefined : '#b91c1c'} />
          </div>

          <div style={{ fontSize: 12.5, fontWeight: 700, margin: '10px 0 6px' }}>Who is signing in</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <tbody>
              {u.by_role.length === 0 ? <tr><td style={{ padding: 8, color: 'var(--color-text-mute)' }}>No users.</td></tr> :
               u.by_role.map(r => (
                <tr key={r.role} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '6px 8px', fontWeight: 600, width: 200 }}>{r.role}</td>
                  <td style={{ padding: '6px 8px', width: 190 }}>
                    {/* "active" meant two different things on one screen — account status, and
                        signed-in-recently. Patrick read "0 of 1 active" as a disabled account when
                        the account is fine and simply has not signed in. Say the measured thing. */}
                    <span style={{ color: r.active_users === 0 ? '#b45309' : 'inherit' }}
                          title={r.active_users === 0
                            ? 'These accounts are active — none of them has signed in during the window'
                            : undefined}>
                      {r.active_users} of {r.users} signed in
                    </span>
                  </td>
                  <td style={{ padding: '6px 8px', width: 110 }}>{r.logins} logins</td>
                  <td style={{ padding: '6px 8px', color: 'var(--color-text-mute)' }}>
                    {r.last_login_at ? `last ${new Date(r.last_login_at).toLocaleDateString()}` : 'never signed in'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Hover readout instead of a native title=. The bars already had a title, but a ~3px
              bar with a one-second OS tooltip is not a readout — Patrick asked to hover and see
              the numbers, which in practice means seeing them immediately and in the page. */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, margin: '14px 0 6px' }}>
            <span style={{ fontSize: 12.5, fontWeight: 700 }}>Login traffic</span>
            <span style={{ fontSize: 12, color: 'var(--color-text-mute)', minHeight: 16 }}>
              {hoverDay
                ? `${new Date(hoverDay.day + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${hoverDay.logins} login${hoverDay.logins === 1 ? '' : 's'}, ${hoverDay.users} user${hoverDay.users === 1 ? '' : 's'}`
                : 'hover a bar for the day’s numbers'}
            </span>
          </div>
          {u.daily.length === 0 ? <EmptyState message="No sign-ins in this window." /> : (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 60 }}
                 onMouseLeave={() => setHoverDay(null)}>
              {u.daily.map(d => {
                const on = hoverDay?.day === d.day
                return (
                  <div key={d.day}
                       onMouseEnter={() => setHoverDay(d)}
                       title={`${d.day}: ${d.logins} logins, ${d.users} users`}
                       style={{ flex: 1, minWidth: 3, height: `${Math.max(4, (d.logins / maxLogins) * 60)}px`,
                                background: on ? '#0e7490' : '#0891b2', opacity: on ? 1 : 0.75,
                                borderRadius: 2, cursor: 'default' }} />
                )
              })}
            </div>
          )}

          <div style={{ fontSize: 12.5, fontWeight: 700, margin: '14px 0 6px' }}>Data feed (PioneerRx ingest)</div>
          {ing.available ? (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}>
              <span>{ing.packages} packages</span><span>·</span>
              <span>{ing.new_rx} new Rx</span><span>·</span>
              <span>{ing.mirrored} mirrored</span><span>·</span>
              <span style={{ color: (ing.deadletter ?? 0) > 0 ? '#b91c1c' : 'inherit', fontWeight: (ing.deadletter ?? 0) > 0 ? 700 : 400 }}>
                {ing.deadletter} dead-lettered
              </span>
              {ing.avg_latency_sec != null && <><span>·</span><span>avg {Math.round(ing.avg_latency_sec)}s latency</span></>}
            </div>
          ) : (
            // NOT an amber warning about the customer. Patrick, 2026-08-22: "the data feed is
            // wrong, we are getting feed from pioneer so saying no ingest is wrong."
            //
            // He is right. dbo.ingest_rollup is created by migration 333 and nothing writes to it —
            // the D-OBS-INGEST worker was never built — so this panel is empty for EVERY customer
            // regardless of their feed. Rendering that in warning-amber next to a real customer
            // made a missing platform feature look like their outage. It is our gap, stated as ours.
            <div style={{ fontSize: 12.5, color: 'var(--color-text-mute)' }}>
              Not collected yet — the ingest rollup that would feed this panel is not running, so
              there is nothing to show for any customer. <b>This is not a statement about their
              PioneerRx feed.</b>
            </div>
          )}

          <div style={{ fontSize: 12.5, fontWeight: 700, margin: '14px 0 6px' }}>Modules — paid for vs used</div>
          {detail.modules.length === 0 ? <EmptyState message="No modules configured." /> : (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {detail.modules.map(m => (
                <span key={m.module} title={m.used ? 'Has recorded use' : 'No recorded use in this window'}
                  style={{
                    fontSize: 12, padding: '3px 9px', borderRadius: 999, border: '1px solid var(--color-border)',
                    background: m.used ? '#ecfdf5' : 'var(--color-surface)',
                    color: m.enabled ? (m.used ? '#047857' : '#b45309') : 'var(--color-text-mute)',
                    textDecoration: m.enabled ? 'none' : 'line-through',
                  }}>
                  {m.module}{m.used ? ' ✓' : ''}
                </span>
              ))}
            </div>
          )}
          <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 6 }}>
            Amber = entitled but <strong>no recorded use</strong> — an upsell/churn signal, though absence of an
            audit record is not proof of non-use.
          </div>

          <div style={{ fontSize: 12.5, fontWeight: 700, margin: '14px 0 6px' }}>What they are doing</div>
          {u.feature_activity.length === 0 ? <EmptyState message="No recorded feature activity in this window." /> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <tbody>
                {u.feature_activity.map(f => (
                  <tr key={f.action} style={{ borderTop: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{f.action}</td>
                    <td style={{ padding: '6px 8px', width: 80 }}>{f.count}</td>
                    <td style={{ padding: '6px 8px', width: 200, color: 'var(--color-text-mute)' }}>
                      last {new Date(f.last_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 8 }}>{u.feature_note}</div>
        </div>)
      })()}

      {tab === 'cases' && (
        open.length === 0 && closed.length === 0 ? <EmptyState message="No cases for this customer." /> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <tbody>
            {[...open, ...closed].map(c => (
              <tr key={c.id} style={{ borderTop: '1px solid var(--color-border)', opacity: ['solved','closed'].includes(c.status) ? 0.6 : 1 }}>
                <td style={{ padding: '6px 8px', width: 70 }}>
                  <button onClick={() => nav(`/admin/support/${c.id}`)}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#0e7490', fontWeight: 700, padding: 0 }}>
                    #{c.ticket_no}
                  </button>
                </td>
                <td style={{ padding: '6px 8px' }}>{c.subject}</td>
                <td style={{ padding: '6px 8px', width: 90 }}>{c.severity}</td>
                <td style={{ padding: '6px 8px', width: 110 }}>{c.status}</td>
                <td style={{ padding: '6px 8px', width: 160, color: 'var(--color-text-mute)' }}>
                  {c.assignee ?? 'unassigned'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>)
      )}

      {tab === 'users' && (
        detail.users.length === 0 ? <EmptyState message="No active users." /> : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <tbody>
            {detail.users.map(u => (
              <tr key={u.email ?? u.display_name ?? Math.random()} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '6px 8px', fontWeight: 600 }}>{u.display_name ?? '—'}</td>
                <td style={{ padding: '6px 8px' }}>{u.email}</td>
                <td style={{ padding: '6px 8px', width: 150 }}>{u.role ?? '—'}</td>
                <td style={{ padding: '6px 8px', width: 90 }}>{u.status}</td>
                <td style={{ padding: '6px 8px', width: 190, color: 'var(--color-text-mute)' }}>
                  {u.last_login_at ? `last login ${new Date(u.last_login_at).toLocaleString()}` : 'never signed in'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>)
      )}

      {tab === 'activity' && (
        detail.activity.length === 0 ? <EmptyState message="No recorded activity for this customer." /> : (
        <>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <tbody>
              {detail.activity.map((a, i) => (
                <tr key={i} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '6px 8px', width: 180, color: 'var(--color-text-mute)' }}>
                    {new Date(a.occurred_at).toLocaleString()}
                  </td>
                  <td style={{ padding: '6px 8px', fontWeight: 600 }}>{a.action}</td>
                  <td style={{ padding: '6px 8px', width: 140 }}>{a.entity_type}</td>
                  <td style={{ padding: '6px 8px', width: 200, color: 'var(--color-text-mute)' }}>
                    {a.actor} {a.actor_role ? `(${a.actor_role})` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 8 }}>{detail.activity_note}</div>
        </>)
      )}
    </div>
  )
}

function MiniKpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 9, padding: '8px 12px', minWidth: 150, background: 'var(--color-surface)' }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-mute)', textTransform: 'uppercase', letterSpacing: '.4px' }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 700, color: tone ?? 'var(--color-text)' }}>{value}</div>
      {sub && <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 1 }}>{sub}</div>}
    </div>
  )
}

// D-SUPPORT-9: the list-level verdict. Hovering gives the REASONS — a band with no explanation is
// something people learn to ignore, and the reasons are what a CSM actually acts on.
function HealthPill({ c }: { c: api.SupportCustomer }) {
  // 'unknown' is handled FIRST and explicitly. The old chain fell through to 'Healthy' for any
  // unrecognised band, so a customer whose health could not be computed would have been shown as
  // green — absence of evidence rendered as good news, which is the failure this whole column
  // exists to prevent.
  const unknown = c.health_band === 'unknown'
  const label = unknown ? 'Unknown' : c.health_band === 'at_risk' ? 'At risk' : c.health_band === 'watch' ? 'Watch' : 'Healthy'
  const tone  = unknown ? '#57534e' : c.health_band === 'at_risk' ? '#b91c1c' : c.health_band === 'watch' ? '#b45309' : '#047857'
  const bg    = unknown ? '#f5f5f4' : c.health_band === 'at_risk' ? '#fef2f2' : c.health_band === 'watch' ? '#fffbeb' : '#ecfdf5'
  // Joined with a bullet separator rather than a newline escape: this file is generated/edited by
  // tooling that has mangled '\n' inside a string literal more than once today.
  const why = c.health_reasons.length ? c.health_reasons.join('  •  ') : 'No risk signals.'
  return (
    <span title={why}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 9px', borderRadius: 999,
               background: bg, color: tone, border: `1px solid ${tone}33`, fontSize: 11.5, fontWeight: 700 }}>
      {label}
      {c.health_reasons.length > 0 && (
        <span style={{ fontWeight: 500, opacity: 0.8 }}>{c.health_reasons.length}</span>
      )}
    </span>
  )
}
